export const SITE = {
  origin: 'https://www.lgrarunning.com',
  name: 'LGRA Running Academy',
  legalName: 'Little Grappy Running Academy',
  defaultTitle: 'Running Coaching Malaysia | Online Plans & Classes | LGRA',
  defaultDescription:
    'LGRA Running Academy: personalised online training plans, running classes for kids, teens & adults in KL and Shah Alam, technique coaching and a free running assessment.',
  ogImage: 'https://www.lgrarunning.com/og-image.jpg',
  logo: 'https://www.lgrarunning.com/lgra-logo.webp',
  phone: '+60176995214',
  phoneDisplay: '+60 17-699 5214',
  whatsapp: '+60162554068',
  whatsappDisplay: '+60 16-255 4068',
  whatsappUrl: 'https://wa.me/60162554068',
  address: { locality: 'Bukit Jalil', region: 'Kuala Lumpur', postalCode: '57000', country: 'MY' },
  socials: {
    facebook: 'https://www.facebook.com/LittleGrappyRunning',
    instagram: 'https://www.instagram.com/lgra_littlegrappyrunning/',
    tiktok: 'https://www.tiktok.com/@littlefernrun',
    youtube: 'https://www.youtube.com/channel/UCTBsW_Rj6sNPZP0vVab_vGw',
  },
};

export const orgJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': ['SportsActivityLocation', 'Organization'],
  '@id': `${SITE.origin}/#organization`,
  name: SITE.legalName,
  alternateName: ['LGRA', 'LGRA Academy', 'LGRA Running Academy'],
  url: `${SITE.origin}/`,
  logo: SITE.logo,
  image: SITE.ogImage,
  telephone: SITE.phone,
  description: SITE.defaultDescription,
  areaServed: { '@type': 'Country', name: 'Malaysia' },
  address: {
    '@type': 'PostalAddress',
    addressLocality: SITE.address.locality,
    addressRegion: SITE.address.region,
    postalCode: SITE.address.postalCode,
    addressCountry: SITE.address.country,
  },
  sameAs: Object.values(SITE.socials),
});

// Any pathname starting with one of these is private / transactional → noindex.
export const PRIVATE_PREFIXES = [
  '/admin', '/coach', '/student', '/dashboard', '/my-', '/login', '/register', '/signup', '/sign-up',
  '/checkout', '/cart', '/guest-checkout', '/guest/', '/pay/', '/payment-', '/booking-confirmation', '/summary/', '/manual-payment',
  '/price-lock', '/training-plan/reservation', '/running-report', '/complete-registration', '/change-password',
  '/waitlist', '/attendance', '/mark-attendance', '/family', '/profile', '/wallet', '/ai-form-check',
  '/ai-running-form-check', '/personal-coach-video-review', '/ai-image-studio', '/ai-mascot-preview', '/magic', '/technique-assessment/status', '/events/paradigm-mall-long-run/registration',
  '/forgot-password', '/reset-password', '/accept-invite', '/auth-callback', '/link-account', '/students',
  '/reports', '/payments', '/find-class-submissions', '/private-enquiries', '/class/', '/runner-market/cart',
  '/runner-market/checkout', '/runner-market/order', '/runner-market/find-order', '/mark-', '/pricelock',
];

// Prefixes ending in '-' or '/' match raw startsWith; others match the exact path or a sub-path (so '/coach' ≠ '/coaches').
export const isPrivatePath = (pathname) => PRIVATE_PREFIXES.some((p) => {
  if (p.endsWith('-') || p.endsWith('/')) return pathname.startsWith(p);
  return pathname === p || pathname.startsWith(`${p}/`);
});

// Legacy aliases that should consolidate onto one canonical URL (no redirects — canonical tag only).
export const CANONICAL_ALIASES = {
  '/buy-packages': '/packages',
  '/private': '/private-booking',
  '/signup': '/register',
  '/sign-up': '/register',
  '/student/register': '/register',
  '/student-portal': '/register',
};

export const normalizePath = (pathname) => {
  const p = (pathname || '/').toLowerCase().replace(/\/+$/, '');
  return p || '/';
};

export const canonicalFor = (pathname) => {
  const p = normalizePath(pathname);
  return `${SITE.origin}${CANONICAL_ALIASES[p] || p}`;
};

const T = (title, description, extra = {}) => ({ title, description, ...extra });

// Static route metadata. Dynamic pages (coach profile, events, service pages) set their own <SeoHead>.
export const ROUTE_SEO = {
  '/': T(SITE.defaultTitle, SITE.defaultDescription, { jsonLd: () => [orgJsonLd()] }),
  '/online-training-plan': T(
    'Online Running Training Plans Malaysia | Free Assessment | LGRA',
    'Get a personalised online running training plan from LGRA coaches. Start with a free running performance assessment and a Running Potential Report, then pick the plan that fits your goal and budget.',
  ),
  '/running-classes': T(
    'Running Classes in Kuala Lumpur & Shah Alam | Kids, Teens, Adults | LGRA',
    'Weekly coached running classes at Bukit Jalil, Lake Gardens KL and Alam Impian: kids and teens development, adult technique and endurance sessions, beginner-friendly community runs.',
  ),
  '/running-classes/kids-teens': T(
    'Kids & Teens Running Classes KL | Ages 5–18 | LGRA',
    'Age-appropriate running classes for children (5–12) and teenagers (13–18) at Bukit Jalil and Lake Gardens: technique, coordination, speed and confidence with LGRA youth coaches.',
  ),
  '/running-classes/adults': T(
    'Adult Running Classes Kuala Lumpur | Technique & Endurance | LGRA',
    'Structured adult running classes in KL and Shah Alam — night sessions, Sunday endurance runs, small-group technique correction. Improve pace, form and stamina with LGRA coaches.',
  ),
  '/running-classes/beginners': T(
    'Beginner Running Classes Malaysia | Start Running with a Coach | LGRA',
    'New to running? Join LGRA community runs and beginner-friendly classes in Bukit Jalil and Alam Impian. Social pace, coach guidance, walk-in friendly.',
  ),
  '/coaching': T(
    'Running Coaching in Malaysia | Private, Technique & Race Prep | LGRA',
    'LGRA running coaching: one-to-one private sessions, running technique correction, strength & mobility for runners and race preparation — in Kuala Lumpur, Shah Alam or online.',
  ),
  '/coaching/private': T(
    'Private Running Coaching KL & Shah Alam | 1-to-1 Sessions | LGRA',
    'Book a private running coach in Kuala Lumpur or Shah Alam. One-to-one or small-group sessions with your preferred LGRA coach, flexible 1–3 hour bookings and hour packages.',
  ),
  '/coaching/running-technique': T(
    'Running Technique Coaching Malaysia | Form Analysis & Strength | LGRA',
    'Improve your running technique with LGRA coaching in Malaysia. Explore running technique assessments, online video reviews, private coaching and workshops.',
  ),
  '/coaching/race-preparation': T(
    'Race Preparation Coaching Malaysia | 5K to Marathon | LGRA',
    'Prepare for your next 5 km, 10 km, half or full marathon with LGRA: personalised training plans, race-specific workshops, pacing strategy and the Malaysian race calendar.',
  ),
  '/all-classes': T(
    'All Running Classes & Live Availability | LGRA',
    'Browse every LGRA running class by category and location — kids, teens, adult, community, personalised group and enhancement classes — with live availability and booking.',
  ),
  '/schedule': T(
    'Weekly Running Class Schedule | Bukit Jalil, Lake Gardens, Alam Impian | LGRA',
    'This week’s LGRA running class schedule across Kuala Lumpur and Shah Alam. Filter by location, coach and class type, then book your spot online.',
  ),
  '/upcomingclass': T(
    'Upcoming Running Classes This Week | LGRA',
    'Open LGRA running classes you can still book this week — live spots, coaches, venues and times.',
  ),
  '/walk-in': T(
    'Walk-In & Trial Running Classes | LGRA',
    'Try LGRA with a walk-in or trial class — no package needed. See which sessions welcome walk-ins this week.',
  ),
  '/trials': T('Trial Running Classes | LGRA', 'Book a trial class with LGRA and experience coached running before committing to a package.'),
  '/coaches': T(
    'Running Coaches in Malaysia | Meet the LGRA Coaching Team',
    'Meet LGRA’s 11 running coaches — head coaches, youth specialists and technique coaches based in Bukit Jalil and Alam Impian. Read profiles and book sessions.',
  ),
  '/race-calendar': T(
    'Malaysia Running Race Calendar 2026 | Upcoming Races | LGRA',
    'Upcoming running races and events in Malaysia — 5K, 10K, half and full marathons, trail and fun runs — with dates, locations and registration links, curated by LGRA.',
  ),
  '/find-my-race': T('Find My Next Race | Malaysia Race Finder | LGRA', 'Answer a few questions and find the Malaysian running race that matches your distance, date and location preferences.'),
  '/runner-market': T('Runner’s Market | Running Gear & Nutrition | LGRA', 'Shop running nutrition, recovery and training essentials curated by LGRA coaches, with pick-up at class or delivery.'),
  '/technique-assessment': T(
    'Online Running & Race Walking Technique Assessment | RM29 WhatsApp or RM59 PDF | LGRA',
    'Send your running or race walking video and get an LGRA coach’s technique assessment — strengths, weaknesses and how to improve. Basic WhatsApp report RM29 or detailed PDF report RM59.',
  ),
  '/events/paradigm-mall-long-run': T(
    'Paradigm Mall LONG RUN — FREE Beginner-friendly Long Run | Sat 31 Oct 2026, 7:30–10:00am | LGRA',
    'LGRA × Emu Oil Naturally × Amazing Seniors: a FREE indoor long run at Paradigm Mall Petaling Jaya with group pacing for every level, long-run briefing and cool-down sharing. FREE participation • Limited slots available • Registration required.',
  ),
  '/membership': T('LGRA Membership | Student Benefits & Validity', 'How LGRA membership works — badge tiers, validity and student benefits for class packages and training plans.'),
  '/packages': T('Class Packages & Credits | LGRA', 'Compare LGRA class packages and credits for kids, teens and adult running classes, and check your remaining balance.'),
  '/private-booking': T('Book a Private Running Session | LGRA', 'Check coach availability and book a private running coaching session with LGRA — choose your coach, venue, date and duration.'),
  '/private-coaching-packages': T('Private Coaching Hour Packages | LGRA', 'Pre-purchase LGRA private coaching hours at a lower hourly rate and use them flexibly across one-to-one or small-group sessions.'),
  '/compare-training-options': T(
    'Compare LGRA Training Options | Classes vs Online Plan vs Private Coaching',
    'Side-by-side comparison of LGRA group classes, online training plans and private coaching — who each is for, how they work and how to start.',
  ),
  '/workshops': T('Running Workshops & Events Malaysia | LGRA', 'Upcoming LGRA running workshops and special events — knee stability, foot strength, technique clinics and more.'),
  '/runners-stories': T('Runners’ Stories | LGRA Student Results', 'Real progress stories from LGRA runners — kids, teens, adults, private coaching and online training plan students.'),
  '/find-my-class': T('Find My Class | Which LGRA Class Fits You?', 'Answer a few questions and we’ll point you to the LGRA class, plan or coaching option that fits your age, level and goals.'),
  '/live-sessions': T('Live Online Running Sessions | LGRA', 'Join LGRA live online sessions and access class recordings wherever you are.'),
  '/info': T('Student Information & Guides | LGRA', 'Practical information for LGRA students — what to bring, class rules, weather policy and more.'),
  '/booking-help': T('How to Book an LGRA Class | Booking Guide', 'Step-by-step guide to booking LGRA running classes online — accounts, family bookings, payment options and confirmations.'),
  '/policies/cancellation-refund': T('Cancellation & Refund Policy | LGRA', 'LGRA cancellation, rescheduling and refund policy for classes, packages and training plans.'),
  '/policies/booking-rules': T('Booking Rules | LGRA', 'LGRA class booking rules — cut-off times, waitlists, walk-ins and attendance.'),
  '/terms': T('Terms & Conditions | LGRA', 'Terms and conditions for using LGRA Running Academy services and website.'),
  '/privacy': T('Privacy Policy | LGRA', 'How LGRA collects, uses and protects your personal data.'),
  '/login': T('Sign In | LGRA', 'Sign in to your LGRA account.', { noindex: true }),
  '/register': T('Create an LGRA Account', 'Create your LGRA student account.', { noindex: true }),
  '/404': T('Page Not Found | LGRA', 'The page you were looking for does not exist.', { noindex: true }),
};

export const breadcrumbJsonLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE.origin}${it.path}`,
  })),
});
